'use client'
import {
    Navigate, useNavigate,
} from "react-router-dom";
import {useCart} from "../Cart/CartContext";
import Navbar from "../Navbar/Navbar";
import {
    useMutation,
    useMutationState,
} from "@tanstack/react-query";
import {useEffect, useState} from "react";
import {PostBody, PostItem} from "./Checkout.dto";
import {toast} from "../ui/use-toast";
import ApiHttpClient from "@/HttpClient";
import {ArticleModel} from "@/api/Models";
import noImageAvailable from "@/assets/images.png";
import {ShoppingCartItems} from "../Cart/cart.dto";
import { getDecodedTokenDetails } from "../Token/TokenService";
import { useLocation } from "react-router-dom";


export default function Checkout() {
    const cartContext = useCart();
    const [workingPointId, setWorkingPointId] = useState('');
    const [validationErrors, setValidationErrors] = useState<Array<{
        articleId: string;
        expectedPrice: number;
        actualPrice: number;
        requestedQuantity: number;
        availableQuantity: number;
        notFound: boolean;
    }> | null>(null);
    const [articleData, setArticleData] = useState<{ [key: string]: { displayName: string; mainLink: string } }>({});
    const navigateToCart = useNavigate();
    const { state } = useLocation();
    const { deliveryMethod, pickupTime } = state || {};

    useEffect(() => {
        (async () => {
            const tokenDetails = await getDecodedTokenDetails();
    
            if (tokenDetails?.token) {
                try {
                    const workingPointUser = tokenDetails.WPIDClaim.toString();
                    setWorkingPointId(workingPointUser);
                } catch (error) {
                    console.error('Failed to decode token', error);
                }
            } else {
                console.error('No token found');
            }
        })();
    }, []);
    

    const articleInfo = async (productId: any): Promise<{ displayName: string; mainLink: string }> => {
        try {
            const response = await ApiHttpClient().get<ArticleModel>(`/article/${productId}`);
            const {displayName, articleImage} = response.data;

            return {
                displayName: displayName || 'Unavailable',
                mainLink: articleImage?.mainLink || '@/assets/images.png',
            };
        } catch (error) {
            console.error(`Error fetching article info for ID: ${productId}`, error);
            return {
                displayName: 'Unavailable',
                mainLink: '@/assets/images.png',
            };
        }
    };

    const handleRedirectToCart = () => {
        navigateToCart("/my-cart");
    }

    const handleAutoAdjustOrder = async () => {
        if (validationErrors && cartContext?.cart?.items) {
            const updatedItems: ShoppingCartItems = {
                stockItems: cartContext.cart.items.stockItems.map((item: any) => {
                    const error = validationErrors.find(
                        (err) => err.articleId === item.article.id
                    );

                    if (error && error.availableQuantity) {
                        const newQuantity = Math.floor(
                            error.availableQuantity / item.article.multiplier
                        );

                        if (newQuantity > 0) {
                            return {
                                ...item,
                                quantity: newQuantity,
                            };
                        }
                    }

                    return item;
                }),
                transitItems: cartContext.cart.items.transitItems.map((item: any) => {
                    const error = validationErrors.find(
                        (err) => err.articleId === item.article.id
                    );

                    if (error && error.availableQuantity) {
                        const newQuantity = Math.floor(
                            error.availableQuantity / item.article.multiplier
                        );

                        if (newQuantity > 0) {
                            return {
                                ...item,
                                quantity: newQuantity,
                            };
                        }
                    }

                    return item;
                }),
            };

            const updatedCart = {
                items: updatedItems,
            };

            cartContext.setCart(updatedCart);

            for (let item of updatedCart?.items.stockItems) {
                await cartContext.updateCartItemQuantity(item.articleId, item.quantity, false);
            }

            for (let item of updatedCart?.items.transitItems) {
                await cartContext.updateCartItemQuantity(item.articleId, item.quantity, true);
            }

            navigateToCart("/my-cart");
        }
    };

    useEffect(() => {
        const fetchArticleData = async () => {
            if (validationErrors) {
                const data = {...articleData};
                for (const error of validationErrors) {
                    if (!data[error.articleId]) {
                        const articleDetails = await articleInfo(error.articleId);
                        data[error.articleId] = articleDetails;
                    }
                }
                setArticleData(data);
            }
        }

        fetchArticleData();
    }, [validationErrors, articleData]);
    
    // Find if other mutations have been shot
    let mutations = useMutationState({filters: {mutationKey: ["checkout"]}});

    let {mutate, isError, isSuccess, isPending} = useMutation({
        mutationFn: (orderMutationBody: PostBody) => {
            return ApiHttpClient().post(
                `/preOrder`,
                orderMutationBody
            );
        },
        mutationKey: ["checkout"],
        onSuccess: (response) => {
            cartContext.setCart({items: {} as ShoppingCartItems});
            toast({description: "Comanda a fost trimisa cu succes"});
            if (response.status === 201) {
                return <Navigate to="/my-account/preorders"/>;
            }
        },
        onError: (error: any) => {
            // console.log('validation errors: ', error);
            const response = error?.response?.data;

            if (response?.articleValidationErrors) {
                setValidationErrors(response.articleValidationErrors);
                //handleClickOpen();
            } else {
                toast({description: "A apărut o eroare la trimiterea comenzii."});
            }
            
            if(response[0].errorMessage === "Shopping cart total exceeds available balance.") {
                toast({description: "Valoarea cosului de cumparaturi este mai mare decat soldul disponibil"});
                navigateToCart("/my-cart");
            }
        },
    });

    useEffect(() => {
        if (!cartContext?.cart) return;

        let isPickup = deliveryMethod === "store";
        const postItemArrStock: PostItem[] = transformCartItems(cartContext.cart.items?.stockItems);
        const postItemArrTransit: PostItem[] = transformCartItems(cartContext.cart.items?.transitItems);

        const postBody: PostBody = {
            warehouseId: 921,
            destinationId: 921,
            workingPointId: workingPointId,
            currency: "RON",
            isPickup,
            timeSlot: pickupTime,
            articles: {
                stockItems: postItemArrStock,
                transitItems: postItemArrTransit,
            },
        };

        if (mutations.length === 0) {
            mutate(postBody);
        }
    }, [cartContext?.cart, workingPointId, deliveryMethod, pickupTime, mutations.length, mutate]);
    
    if (mutations.length > 0) {
        isError = mutations[0].status === "error";
        isSuccess = mutations[0].status === "success";
        isPending = mutations[0].status === "pending";
    }

    if (isSuccess) {
        return <Navigate to="/my-account/preorders"/>;
    }

    return (
        <>
            <div>
                <Navbar/>
            </div>
            <div className="grid place-content-center bg-white px-4 mt-10 my-10">
                <h1 className="uppercase tracking-wides">
                    {isPending ? (
                        "Se incarca...."
                    ) : (
                        <>
                            {isError ?
                                <h1 className="uppercase tracking-widest text-gray-500 mb-5">Ne pare rau, a aparut o
                                    eroare.</h1>
                                : null}
                            {/* {isSuccess
                //? `Comanda cu id ${cartMetadata?.sessionKey} a fost trimisa cu succes!`
                ? redirect('/my-account/orders')
                : null} */}
                            {validationErrors && (
                                <div>
                                    <ul>
                                        {
                                            validationErrors.map((error: any) => (
                                                (!error.notFound && error.expectedPrice === null) &&
                                                <article className="rounded-xl border-2 border-gray-100 bg-white my-2">
                                                    <div className="flex items-start gap-4 p-4 sm:p-6 lg:p-8">
                                                        <div className="block shrink-0">
                                                            {articleData[error.articleId]?.mainLink ? <img
                                                                alt=""
                                                                src={articleData[error.articleId]?.mainLink}
                                                                className="size-14 rounded-lg object-cover"
                                                            /> : <img
                                                                alt=""
                                                                src={noImageAvailable}
                                                                className="size-14 rounded-lg object-cover"
                                                            />}
                                                        </div>

                                                        {<div>
                                                            <h3 className="font-medium sm:text-lg">
                                                                <a href={"/product/" + error.articleId}
                                                                   className="hover:underline">{error.articleId} - {articleData[error.articleId]?.displayName || 'Loading...'} </a>
                                                            </h3>

                                                            <p className="line-clamp-2 text-sm text-red-700">Stoc
                                                                insuficient</p>

                                                            <div className="mt-2 sm:flex sm:items-center sm:gap-2">
                                                                <div
                                                                    className="flex items-center gap-1 text-gray-500">

                                                                    <p className="hidden sm:block sm:text-xs sm:text-gray-500">
                                                                        Cantitate comandata
                                                                        <span
                                                                            className="font-medium text-red-500 underline hover:text-gray-700"> {error.requestedQuantity} </span>
                                                                    </p>
                                                                </div>

                                                                <span className="hidden sm:block"
                                                                      aria-hidden="true">&middot;</span>

                                                                <p className="hidden sm:block sm:text-xs sm:text-gray-500">
                                                                    Cantitate disponibila
                                                                    <span
                                                                        className="font-medium text-green-600 underline hover:text-gray-700"> {error.availableQuantity} </span>
                                                                </p>
                                                            </div>

                                                        </div>}
                                                    </div>
                                                </article>
                                            ))}
                                    </ul>
                                    <ul>
                                        {
                                            validationErrors.map((error: any) => (
                                                (error.notFound) &&
                                                <article className="rounded-xl border-2 border-gray-100 bg-white my-2">
                                                    <div className="flex items-start gap-4 p-4 sm:p-6 lg:p-8">
                                                        <div className="block shrink-0">
                                                            {articleData[error.articleId]?.mainLink ? <img
                                                                alt=""
                                                                src={articleData[error.articleId]?.mainLink}
                                                                className="size-14 rounded-lg object-cover"
                                                            /> : <img
                                                                alt=""
                                                                src={noImageAvailable}
                                                                className="size-14 rounded-lg object-cover"
                                                            />}
                                                        </div>

                                                        {<div>
                                                            <h3 className="font-medium sm:text-lg">
                                                                <a href={"/product/" + error.articleId}
                                                                   className="hover:underline">{error.articleId} - {articleData[error.articleId]?.displayName || 'Loading...'} </a>
                                                            </h3>

                                                            <p className="line-clamp-2 text-sm text-red-700">Stoc
                                                                epuizat</p>

                                                            <p className="line-clamp-2 text-sm text-gray-700">
                                                                Nu mai exista stoc disponibil pentru acest produs.
                                                            </p>

                                                        </div>}
                                                    </div>
                                                </article>
                                            ))}
                                    </ul>

                                    <ul>
                                        {
                                            validationErrors.map((error: any) => (
                                                (error.expectedPrice !== null) &&
                                                <article className="rounded-xl border-2 border-gray-100 bg-white my-2">
                                                    <div className="flex items-start gap-4 p-4 sm:p-6 lg:p-8">
                                                        <div className="block shrink-0">
                                                            {articleData[error.articleId]?.mainLink ? <img
                                                                alt=""
                                                                src={articleData[error.articleId]?.mainLink}
                                                                className="size-14 rounded-lg object-cover"
                                                            /> : <img
                                                                alt=""
                                                                src={noImageAvailable}
                                                                className="size-14 rounded-lg object-cover"
                                                            />}
                                                        </div>

                                                        {<div>
                                                            <h3 className="font-medium sm:text-lg">
                                                                <a href={"/product/" + error.articleId}
                                                                   className="hover:underline">{error.articleId} - {articleData[error.articleId]?.displayName || 'Loading...'} </a>
                                                            </h3>

                                                            <p className="line-clamp-2 text-sm text-red-700">Pret
                                                                modificat</p>

                                                            <div className="mt-2 sm:flex sm:items-center sm:gap-2">
                                                                <div className="flex items-center gap-1 text-gray-500">

                                                                    <p className="hidden sm:block sm:text-xs sm:text-gray-500">
                                                                        Pretul comandat
                                                                        <span
                                                                            className="font-medium text-red-500 underline hover:text-gray-700"> {error.expectedPrice} </span>
                                                                    </p>
                                                                </div>

                                                                <span className="hidden sm:block"
                                                                      aria-hidden="true">&middot;</span>

                                                                <p className="hidden sm:block sm:text-xs sm:text-gray-500">
                                                                    Pretul actual
                                                                    <span
                                                                        className="font-medium text-green-600 underline hover:text-gray-700"> {error.actualPrice} </span>
                                                                </p>
                                                            </div>

                                                        </div>}
                                                    </div>
                                                </article>
                                            ))}
                                    </ul>
                                    <form className="mt-4 flex gap-4">
                                        <button
                                            className="block w-full rounded bg-gray-100 px-2 py-2 text-sm font-medium text-gray-900 transition hover:scale-90"
                                            onClick={handleRedirectToCart}
                                        >
                                            Catre Cos
                                        </button>

                                        <button
                                            type="button"
                                            className="block w-full rounded bg-red-500 px-2 py-2 text-sm font-medium text-white transition hover:scale-90"
                                            onClick={handleAutoAdjustOrder}
                                        >
                                            Modifica automat cantitatea
                                        </button>
                                    </form>
                                </div>
                            )}
                        </>
                    )}
                </h1>
            </div>
        </>
    );
}

function transformCartItems(cartItems: any) {
    if (!cartItems) {
        return [];
    }
    return cartItems.map((pd: any, index: any) => {
        const quantity = Number.parseFloat((pd.quantity * pd.article.multiplier).toFixed(2));

        const key = `thresholdPrice${pd.thresholdPriceIndex}`;
        const salesPrice = pd.article[key];
        
        return {
            articleId: pd.article.id,
            lineNumber: index + 1,
            salesUnit: pd.article.salesUnit,
            quantity: quantity > 0 ? quantity : 1, // ordered quantity is quantity * multiplier
            salesPrice: salesPrice,
            thresholdPriceIndex: pd.thresholdPriceIndex,
        };
    });
}
